/** XeAgents.ai**/
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Link, FileText, Type, Search, ChevronDown, FileStack, Trash2, Upload, Globe, FileType, Crown, Zap, Lock, BookOpen, Loader2, CheckCircle2, AlertCircle, Brain, RefreshCw, FolderPlus, Sparkles, HardDrive, Bot } from "lucide-react";
import { AuthStorage } from "@/lib/auth-storage";
import { formatDate } from "@/lib/date-format";
// The same folder normalizer the server uses. This page compared raw strings, so
// "Sales" and "/Sales" filtered as different folders while the sidebar counted them
// as one.
import {
  folderPathKey,
  isRootFolderPath,
  knowledgeItemFolderPath,
  folderDisplayName,
} from "@shared/knowledge-base-path";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { DataPagination, usePagination } from "@/components/ui/data-pagination";

interface KnowledgeBaseItem {
  id: string;
  type: string;
  title: string;
  content?: string;
  url?: string;
  fileUrl?: string;
  storageSize: number;
  createdAt: string;
  ragStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  ragProgress?: number;
  chunkCount?: number;
  isRAGEnabled?: boolean;
  metadata?: any;
  version?: number;
  documentGroup?: string;
  isLatest?: boolean;
  /** Why processing failed, when ragStatus is 'failed'. */
  ragError?: string;
  /** Names of the agents that currently retrieve from this document. */
  usedByAgents?: string[];
  agentCount?: number;
}

interface DuplicateError {
  error: string;
  message: string;
  existingDocument: {
    id: string;
    title: string;
    createdAt: string;
  };
  actions: Array<{
    action: string;
    label: string;
  }>;
}

interface StorageUsage {
  maxStorageBytes: number;
  usedStorageBytes: number;
  remainingBytes: number;
  usagePercent: number;
}

interface User {
  id: string;
  email: string;
  name: string;
  planType: string;
  credits: number;
}

export default function KnowledgeBase() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [folderFilter, setFolderFilter] = useState<string>('/');
  
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [newFolderPath, setNewFolderPath] = useState('');
  
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [movingItem, setMovingItem] = useState<KnowledgeBaseItem | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<string>('/');
  const [newFolderName, setNewFolderName] = useState('');

  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [fileFolder, setFileFolder] = useState<string>('');
  const [fileNewFolder, setFileNewFolder] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pasted text as a source. POST /text existed with no UI.
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [textName, setTextName] = useState("");
  const [textBody, setTextBody] = useState("");
  const [textFolder, setTextFolder] = useState<string>('');

  // "Test a question" — run the retrieval an agent would run, against a folder.
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testFolder, setTestFolder] = useState<string>('');
  const [testQuestion, setTestQuestion] = useState("");
  const [testResults, setTestResults] = useState<{
    results: Array<{ text: string; score: number; source: string }>;
    confidence?: string;
    disclaimer?: string;
  } | null>(null);

  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [urlName, setUrlName] = useState("");
  const [urlFolder, setUrlFolder] = useState<string>('');
  const [urlNewFolder, setUrlNewFolder] = useState('');
  
  const [deletingItem, setDeletingItem] = useState<KnowledgeBaseItem | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<string | null>(null);
  
  const [duplicateDialog, setDuplicateDialog] = useState<{
    open: boolean;
    error: DuplicateError | null;
    pendingAction: 'upload' | 'url' | 'text' | null;
  }>({ open: false, error: null, pendingAction: null });
  
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const [viewingVersionsFor, setViewingVersionsFor] = useState<KnowledgeBaseItem | null>(null);
  const [versions, setVersions] = useState<KnowledgeBaseItem[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  
  const { toast } = useToast();

  const { data: user, isLoading: userLoading, isError: userError } = useQuery<User>({
    queryKey: ['/api/auth/me'],
  });

  const { data: storageUsage } = useQuery<StorageUsage>({
    queryKey: ["/api/rag-knowledge/storage"],
  });

  const { data: knowledgeBase = [], isLoading, refetch } = useQuery<KnowledgeBaseItem[]>({
    queryKey: ["/api/rag-knowledge"],
    refetchInterval: (query) => {
      const items = query.state.data as KnowledgeBaseItem[] | undefined;
      const hasProcessingItems = items?.some(item => 
        item.ragStatus === 'processing' || item.ragStatus === 'pending'
      );
      return hasProcessingItems ? 3000 : false;
    },
  });

  // Folders now return objects with path and count, excluding root "/".
  // count is CURRENT versions only, and agentCount is how many agents select the folder.
  const { data: folders = [] } = useQuery<
    Array<{ path: string; count: number; agentCount: number; isEmpty: boolean }>
  >({
    queryKey: ["/api/rag-knowledge/folders"],
  });

  // Knowledge base limits (folder count per plan, per-folder size)
  const { data: kbLimits } = useQuery<{
    folders: { current: number; max: number; remaining: number };
    perFolderMaxBytes: number;
    folderSizes: Record<string, number>;
  }>({
    queryKey: ["/api/rag-knowledge/limits"],
  });

  // Get available folders (excluding root)
  const availableFolders = folders.filter(f => {
    const path = typeof f === 'string' ? f : (f as any).path;
    return path && path !== '/';
  });

  // Auto-select the first available folder when dialog opens (better UX)
  useEffect(() => {
    if (fileDialogOpen && availableFolders.length > 0 && !fileFolder) {
      const folderPath = typeof availableFolders[0] === 'string'
        ? availableFolders[0]
        : (availableFolders[0] as any).path;
      setFileFolder(folderPath);
    }
  }, [fileDialogOpen, availableFolders, fileFolder]);

  useEffect(() => {
    if (urlDialogOpen && availableFolders.length > 0 && !urlFolder) {
      const folderPath = typeof availableFolders[0] === 'string'
        ? availableFolders[0]
        : (availableFolders[0] as any).path;
      setUrlFolder(folderPath);
    }
  }, [urlDialogOpen, availableFolders, urlFolder]);

  const createFolderMutation = useMutation({
    mutationFn: async (data: { folderPath: string }) => {
      // Pre-check folder count limit
      if (kbLimits && kbLimits.folders.remaining <= 0) {
        throw new Error(`Folder limit reached. Your plan allows a maximum of ${kbLimits.folders.max} folders. Please upgrade your plan or delete existing folders.`);
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }
      
      const response = await fetch('/api/rag-knowledge/folders', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ folderPath: data.folderPath }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to create folder');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      // Immediately update the folders query cache with the new folder
      queryClient.setQueryData(
        ['/api/rag-knowledge/folders'],
        (oldFolders: Array<{ path: string; count: number }> | undefined) => {
          // Filter out the default /General placeholder (count 0) since a real folder is being created
          const current = (oldFolders || []).filter(
            f => !(f.path === '/General' && f.count === 0)
          );
          // Check if folder already exists in the list
          if (!current.some(f => f.path === data.folderPath)) {
            return [...current, { path: data.folderPath, count: 0 }];
          }
          return current;
        }
      );
      
      queryClient.invalidateQueries({ queryKey: ["/api/rag-knowledge/limits"] });
      setCreateFolderDialogOpen(false);
      setNewFolderPath('');
      setFileFolder(data.folderPath);
      toast({
        title: 'Folder Created',
        description: 'Folder created successfully. You can now upload files to it.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to Create Folder',
        description: error.message || 'Failed to create folder',
        variant: 'destructive',
      });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (folderPath: string) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }

      // Strip leading slash for URL encoding to avoid %2F issues, backend will normalize
      const pathForUrl = folderPath.startsWith('/') ? folderPath.substring(1) : folderPath;

      const response = await fetch(`/api/rag-knowledge/folders/${encodeURIComponent(pathForUrl)}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to delete folder');
      }

      return response.json();
    },
    onSuccess: (_data, folderPath) => {
      queryClient.refetchQueries({ queryKey: ['/api/rag-knowledge/folders'] });
      queryClient.refetchQueries({ queryKey: ['/api/rag-knowledge'] });
      queryClient.refetchQueries({ queryKey: ['/api/rag-knowledge/storage'] });
      queryClient.refetchQueries({ queryKey: ['/api/rag-knowledge/limits'] });
      if (folderFilter === folderPath) {
        setFolderFilter('/');
      }
      setDeletingFolder(null);
      toast({
        title: 'Folder Deleted',
        description: 'Folder and all its documents have been deleted.',
      });
    },
    onError: (error: any) => {
      // Refresh queries even on error so UI stays in sync (e.g. 404 = already deleted)
      queryClient.refetchQueries({ queryKey: ['/api/rag-knowledge/folders'] });
      queryClient.refetchQueries({ queryKey: ['/api/rag-knowledge/limits'] });
      toast({
        title: 'Delete Folder',
        description: error.message || 'Folder not found or already deleted.',
        variant: 'destructive',
      });
    },
  });

  const moveMutation = useMutation({
    mutationFn: async (data: { id: string; folderPath: string }) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }
      
      const response = await fetch(`/api/rag-knowledge/${data.id}/move`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ folderPath: data.folderPath }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to move item');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge'] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/folders'] });
      setMoveDialogOpen(false);
      setMovingItem(null);
      setNewFolderName('');
      toast({
        title: 'Item Moved',
        description: 'Document moved to folder successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Move Failed',
        description: 'Failed to move document',
        variant: 'destructive',
      });
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: async (data: { file: File; name?: string; folderPath: string; replaceId?: string }) => {
      const formData = new FormData();
      formData.append('file', data.file);
      if (data.name) {
        formData.append('name', data.name);
      }
      formData.append('folderPath', data.folderPath);
      
      const headers: Record<string, string> = {};
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }
      
      const endpoint = data.replaceId 
        ? `/api/rag-knowledge/${data.replaceId}/replace`
        : '/api/rag-knowledge/upload';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          const duplicateError = new Error('Duplicate detected');
          (duplicateError as any).isDuplicate = true;
          (duplicateError as any).data = errorData;
          throw duplicateError;
        }
        const uploadError = new Error(errorData.error || t('knowledgeBase.errors.uploadFailed'));
        (uploadError as any).code = errorData.code;
        (uploadError as any).existingFolders = errorData.existingFolders;
        throw uploadError;
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge'] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/storage'] });
      queryClient.invalidateQueries({ queryKey: ["/api/rag-knowledge/limits"] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/folders'] });
      setFileDialogOpen(false);
      setSelectedFile(null);
      setFileName('');
      setFileFolder('');
      setFileNewFolder('');
      toast({
        title: t('knowledgeBase.toast.fileUploaded'),
        description: t('knowledgeBase.toast.fileUploadedDesc'),
      });
    },
    onError: (error: any) => {
      if (error.isDuplicate) {
        setDuplicateDialog({
          open: true,
          error: error.data,
          pendingAction: 'upload',
        });
      } else if (error.code === 'FOLDER_LIMIT_EXCEEDED' && error.existingFolders?.length) {
        const folderNames = (error.existingFolders as string[])
          .map((p: string) => p.replace(/^\/+/, ''))
          .join(', ');
        toast({
          title: 'Folder Limit Reached',
          description: `Your plan allows ${kbLimits?.folders.max ?? 1} folder(s). Please open the "All Folders" dropdown and delete the existing folder "${folderNames}" first, then try again.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: t('knowledgeBase.toast.uploadFailed'),
          description: error.message || t('knowledgeBase.toast.uploadFailedDesc'),
          variant: "destructive",
        });
      }
    },
  });

  const resetUrlDialog = () => {
    setUrlValue('');
    setUrlName('');
    setUrlFolder('');
    setUrlNewFolder('');
  };

  const addUrlMutation = useMutation({
    mutationFn: async (data: { url: string; name?: string; folderPath: string }) => {
      const res = await apiRequest('POST', '/api/rag-knowledge/url', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge'] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/storage'] });
      queryClient.invalidateQueries({ queryKey: ["/api/rag-knowledge/limits"] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/folders'] });
      setUrlDialogOpen(false);
      resetUrlDialog();
      toast({
        title: t('knowledgeBase.toast.urlAdded'),
        description: t('knowledgeBase.toast.urlAddedDesc'),
      });
    },
    onError: (error: any) => {
      const data = error?.data;

      if (error?.status === 409 && data) {
        setDuplicateDialog({
          open: true,
          error: data,
          pendingAction: 'url',
        });
        return;
      }

      if (data?.code === 'FOLDER_LIMIT_EXCEEDED' && data.existingFolders?.length) {
        const folderNames = (data.existingFolders as string[])
          .map((path: string) => path.replace(/^\/+/, ''))
          .join(', ');
        toast({
          title: 'Folder Limit Reached',
          description: `Your plan allows ${kbLimits?.folders.max ?? 1} folder(s). Please open the "All Folders" dropdown and delete the existing folder "${folderNames}" first, then try again.`,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: t('knowledgeBase.toast.urlFailed'),
        description: error?.message || t('knowledgeBase.toast.urlFailedDesc'),
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('DELETE', `/api/rag-knowledge/${id}`);
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge'] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/storage'] });
      queryClient.invalidateQueries({ queryKey: ["/api/rag-knowledge/limits"] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/folders'] });
      // The server detaches the document from agents in the same request. Refresh the
      // agent list so an open Agents page does not keep showing the old selection.
      queryClient.invalidateQueries({ queryKey: ['/api/agents'] });
      setDeletingItem(null);
      toast({
        title: t('common.delete'),
        description: data?.updatedAgents
          ? `${t('knowledgeBase.toast.deleted')} ${data.updatedAgents} agent(s) updated.`
          : t('knowledgeBase.toast.deleted'),
      });
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('knowledgeBase.toast.deleteFailed'),
        variant: "destructive",
      });
    },
  });

  // Pasted text as a knowledge source.
  const addTextMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/rag-knowledge/text', {
        name: textName,
        text: textBody,
        folderPath: textFolder,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge'] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/storage'] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/limits'] });
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge/folders'] });
      // Agents selecting this folder pick the new document up server-side.
      queryClient.invalidateQueries({ queryKey: ['/api/agents'] });
      setTextDialogOpen(false);
      setTextName('');
      setTextBody('');
      setTextFolder('');
      toast({ title: 'Text added', description: 'Processing embeddings in the background.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Could not add text',
        description: error?.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Run the same retrieval an agent would run, so a user can check a folder answers a
  // real question before trusting a call to it.
  const testSearchMutation = useMutation({
    mutationFn: async () => {
      // The folder is resolved to its document IDs here, exactly as the agent routes do
      // server-side — the /search endpoint takes IDs, not a folder.
      const ids = knowledgeBase
        .filter(item => folderPathKey(knowledgeItemFolderPath(item)) === folderPathKey(testFolder))
        .map(item => item.id);

      if (ids.length === 0) {
        throw new Error('That folder has no documents to search.');
      }

      const res = await apiRequest('POST', '/api/rag-knowledge/search', {
        query: testQuestion,
        knowledgeBaseIds: ids,
        maxResults: 5,
      });
      return res.json();
    },
    onSuccess: (data: any) => setTestResults(data),
    onError: (error: any) => {
      setTestResults(null);
      toast({
        title: 'Search failed',
        description: error?.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Retry a document whose embedding run failed.
  //
  // POST /:id/reprocess and POST /reprocess-failed have existed since the RAG routes
  // were written and had no UI at all, so a failed document could only be deleted and
  // re-uploaded — and until it was, any agent selecting its folder silently retrieved
  // nothing from it.
  const reprocessMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('POST', `/api/rag-knowledge/${id}/reprocess`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge'] });
      toast({
        title: 'Reprocessing started',
        description: 'Embeddings are being regenerated. The status updates automatically.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Reprocessing failed',
        description: error?.message || 'Could not start reprocessing. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Maximum file size: 10 MB (matches server-side multer configuration)
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Enforce 10 MB file size limit
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast({
          title: 'File Too Large',
          description: `Maximum file size is 10 MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)} MB.`,
          variant: "destructive",
        });
        // Clear the input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setSelectedFile(file);
      if (!fileName) {
        setFileName(file.name);
      }
    }
  };

  const handleUploadFile = (replaceId?: string) => {
    if (!selectedFile) {
      toast({
        title: t('knowledgeBase.toast.noFileSelected'),
        description: t('knowledgeBase.toast.noFileSelectedDesc'),
        variant: "destructive",
      });
      return;
    }

    // Use new folder if provided, otherwise use selected folder
    const targetFolder = fileNewFolder.trim() || fileFolder;

    // Require a folder - cannot upload without one
    if (!targetFolder || targetFolder === '/') {
      toast({
        title: 'Step 1: Select a Folder',
        description: 'Please select an existing folder or create a new one in Step 1 before uploading.',
        variant: 'destructive',
      });
      return;
    }

    // Pre-check per-folder 50 MB limit
    if (kbLimits && targetFolder && targetFolder !== '/') {
      const folderKey = targetFolder.startsWith('/') ? targetFolder : `/${targetFolder}`;
      const currentFolderSize = kbLimits.folderSizes[folderKey] || 0;
      if (currentFolderSize + selectedFile.size > kbLimits.perFolderMaxBytes) {
        toast({
          title: 'Folder Size Limit Exceeded',
          description: `This folder has ${(currentFolderSize / (1024 * 1024)).toFixed(1)} MB used. Adding this file (${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB) would exceed the 25 MB per-folder limit.`,
          variant: 'destructive',
        });
        return;
      }
    }

    uploadFileMutation.mutate({
      file: selectedFile, 
      name: fileName || selectedFile.name,
      folderPath: targetFolder,
      replaceId
    });
  };

  const handleAddUrl = () => {
    const trimmedUrl = urlValue.trim();

    if (!trimmedUrl) {
      toast({
        title: t('knowledgeBase.toast.urlRequired'),
        description: t('knowledgeBase.toast.urlRequiredDesc'),
        variant: "destructive",
      });
      return;
    }

    // Mirror the server-side check so obvious mistakes never leave the browser
    let parsedUrl: URL;
    try {
      parsedUrl = new URL(trimmedUrl);
    } catch {
      toast({
        title: t('knowledgeBase.toast.urlRequired'),
        description: 'Enter a full website address, for example https://example.com/pricing',
        variant: "destructive",
      });
      return;
    }

    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      toast({
        title: t('knowledgeBase.toast.urlRequired'),
        description: 'Only http:// and https:// addresses can be fetched.',
        variant: "destructive",
      });
      return;
    }

    // Use new folder if provided, otherwise use selected folder
    const targetFolder = urlNewFolder.trim() || urlFolder;

    if (!targetFolder || targetFolder === '/') {
      toast({
        title: 'Step 1: Select a Folder',
        description: 'Please select an existing folder or create a new one in Step 1 before adding a URL.',
        variant: 'destructive',
      });
      return;
    }

    addUrlMutation.mutate({
      url: trimmedUrl,
      name: urlName.trim() || undefined,
      folderPath: targetFolder,
    });
  };

  const handleDuplicateAction = (action: string) => {
    if (action === 'replace' && duplicateDialog.error) {
      const existingId = duplicateDialog.error.existingDocument.id;
      
      if (duplicateDialog.pendingAction === 'upload' && selectedFile) {
        handleUploadFile(existingId);
      } else if (duplicateDialog.pendingAction === 'url') {
        // For URL/text, we would need to implement replace endpoints for those too
        toast({
          title: 'Not Implemented',
          description: 'Replace is only supported for file uploads currently',
          variant: 'destructive',
        });
      }
      
      setDuplicateDialog({ open: false, error: null, pendingAction: null });
    } else if (action === 'keep_both') {
      // Proceed with upload as new document
      // This would require modifying the upload to skip duplicate check
      toast({
        title: 'Not Implemented',
        description: 'Force upload option will be available soon',
        variant: 'destructive',
      });
      setDuplicateDialog({ open: false, error: null, pendingAction: null });
    } else {
      // Cancel
      setDuplicateDialog({ open: false, error: null, pendingAction: null });
    }
  };

  const handleViewVersions = async (item: KnowledgeBaseItem) => {
    setViewingVersionsFor(item);
    setVersionsDialogOpen(true);
    setVersionsLoading(true);
    
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }
      
      const response = await fetch(`/api/rag-knowledge/${item.id}/versions`, {
        headers,
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch versions');
      }
      
      const data = await response.json();
      setVersions(data);
    } catch (error: any) {
      toast({
        title: 'Failed to Load Versions',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleRestoreVersion = async (versionId: string) => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }
      
      const response = await fetch(`/api/rag-knowledge/${versionId}/restore`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to restore version');
      }
      
      queryClient.invalidateQueries({ queryKey: ['/api/rag-knowledge'] });
      setVersionsDialogOpen(false);
      setViewingVersionsFor(null);
      
      toast({
        title: 'Version Restored',
        description: 'Document has been restored to the selected version',
      });
    } catch (error: any) {
      toast({
        title: 'Restore Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const filteredItems = knowledgeBase.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !typeFilter || item.type === typeFilter;
    // Compared through the shared normalizer. This used to compare RAW strings, so a
    // document stored as "Sales" was filtered out of the folder the sidebar called
    // "/Sales" — the folder's count said 3 and the list showed 1.
    const matchesFolder =
      isRootFolderPath(folderFilter) ||
      folderPathKey(knowledgeItemFolderPath(item)) === folderPathKey(folderFilter);
    return matchesSearch && matchesType && matchesFolder;
  });

  const {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    paginatedItems,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filteredItems, 10);

  const totalSize = knowledgeBase.reduce((sum, item) => sum + item.storageSize, 0);
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'url':
        return <Globe className="h-4 w-4" />;
      case 'file':
        return <FileText className="h-4 w-4" />;
      case 'text':
        return <Type className="h-4 w-4" />;
      default:
        return <FileType className="h-4 w-4" />;
    }
  };

  const getTypeBadgeVariant = (type: string): "default" | "secondary" | "outline" => {
    switch (type) {
      case 'url':
        return 'default';
      case 'file':
        return 'secondary';
      case 'text':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Loader2 className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusText = (status: string | undefined) => {
    switch (status) {
      case 'processing':
        return t('knowledgeBase.status.processing');
      case 'completed':
        return t('knowledgeBase.status.ready');
      case 'failed':
        return t('knowledgeBase.status.failed');
      default:
        return t('knowledgeBase.status.pending');
    }
  };

  // Helper function to get folder usage color based on percentage
  const getFolderUsageColor = (folderPath: string): { bg: string; text: string; border: string; indicator: string } => {
    if (!kbLimits || !folderPath || folderPath === '/') {
      return { bg: 'bg-gray-50 dark:bg-gray-900', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700', indicator: 'bg-gray-400' };
    }

    const folderKey = folderPath.startsWith('/') ? folderPath : `/${folderPath}`;
    const usedBytes = kbLimits.folderSizes[folderKey] || 0;
    const usagePercent = (usedBytes / kbLimits.perFolderMaxBytes) * 100;

    if (usagePercent >= 90) {
      return { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800', indicator: 'bg-red-500' };
    } else if (usagePercent >= 70) {
      return { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800', indicator: 'bg-amber-500' };
    } else if (usagePercent >= 50) {
      return { bg: 'bg-yellow-50 dark:bg-yellow-950/30', text: 'text-yellow-700 dark:text-yellow-400', border: 'border-yellow-200 dark:border-yellow-800', indicator: 'bg-yellow-500' };
    } else if (usagePercent > 0) {
      return { bg: 'bg-green-50 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-800', indicator: 'bg-green-500' };
    }
    return { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800', indicator: 'bg-blue-400' };
  };

  // Helper function to get folder usage percentage
  const getFolderUsagePercent = (folderPath: string): number => {
    if (!kbLimits || !folderPath || folderPath === '/') return 0;
    const folderKey = folderPath.startsWith('/') ? folderPath : `/${folderPath}`;
    const usedBytes = kbLimits.folderSizes[folderKey] || 0;
    return Math.round((usedBytes / kbLimits.perFolderMaxBytes) * 100);
  };

  // Helper function to get folder used size formatted
  const getFolderUsedSize = (folderPath: string): string => {
    if (!kbLimits || !folderPath || folderPath === '/') return '0 MB';
    const folderKey = folderPath.startsWith('/') ? folderPath : `/${folderPath}`;
    const usedBytes = kbLimits.folderSizes[folderKey] || 0;
    return `${(usedBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper function to get type badge color classes
  const getTypeBadgeColor = (type: string): string => {
    switch (type) {
      case 'url':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'file':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
      case 'text':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 border-purple-200 dark:border-purple-800';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300 border-gray-200 dark:border-gray-800';
    }
  };

  // Helper function to get status badge color classes
  const getStatusBadgeColor = (status: string | undefined): string => {
    switch (status) {
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 border-green-200 dark:border-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300 border-red-200 dark:border-red-800';
      default:
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 border-amber-200 dark:border-amber-800';
    }
  };

  // Helper function to format folder display name (remove slashes)
  const formatFolderDisplay = (folderPath: string): string => {
    if (!folderPath || folderPath === '/') return 'Root';
    // Remove leading and trailing slashes
    return folderPath.replace(/^\/+|\/+$/g, '');
  };

  /**
   * Step 1 of the add-content dialogs: pick an existing folder or create a new one.
   * Shared by the file upload and website URL dialogs.
   */
  const renderFolderStep = (
    folder: string,
    setFolder: (value: string) => void,
    newFolder: string,
    setNewFolder: (value: string) => void,
  ) => (
    <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border border-violet-200 dark:border-violet-800 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-bold">1</div>
        <h4 className="font-semibold text-violet-900 dark:text-violet-100">Select or Create Folder</h4>
        <Badge variant="destructive" className="ml-auto text-xs">Required</Badge>
      </div>

      {/* Show existing folders or create new */}
      {folders.filter(f => (typeof f === 'string' ? f : (f as any).path) !== '/').length > 0 ? (
        <div className="space-y-3">
          <Label className="text-sm text-violet-700 dark:text-violet-300">Choose existing folder:</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={`w-full justify-between text-sm ${folder ? getFolderUsageColor(folder).bg : 'bg-white dark:bg-slate-900'}`}
              >
                <span className="flex items-center gap-2">
                  {folder ? (
                    <>
                      <span className={`w-2 h-2 rounded-full ${getFolderUsageColor(folder).indicator}`} />
                      📁 {formatFolderDisplay(folder)} ({getFolderUsagePercent(folder)}% used)
                    </>
                  ) : (
                    <span className="text-muted-foreground">-- Select a folder --</span>
                  )}
                </span>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
              {folders.filter(f => (typeof f === 'string' ? f : (f as any).path) !== '/').map(folderEntry => {
                const folderPath = typeof folderEntry === 'string' ? folderEntry : (folderEntry as any).path || '/';
                const folderCount = typeof folderEntry === 'object' ? (folderEntry as any).count || 0 : 0;
                const usageColors = getFolderUsageColor(folderPath);
                const usagePercent = getFolderUsagePercent(folderPath);
                return (
                  <DropdownMenuItem
                    key={folderPath}
                    className={`flex items-center justify-between gap-2 ${usageColors.bg} ${usageColors.border} border rounded-md my-1 mx-1 cursor-pointer`}
                    onSelect={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest('button[data-delete-folder]')) {
                        e.preventDefault();
                        return;
                      }
                      setFolder(folderPath);
                      setNewFolder('');
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <span className={`w-2.5 h-2.5 rounded-full ${usageColors.indicator}`} />
                      <span className={`font-medium ${usageColors.text}`}>
                        📁 {formatFolderDisplay(folderPath)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({folderCount} docs, {usagePercent}%)
                      </span>
                    </div>
                    <button
                      data-delete-folder="true"
                      className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setDeletingFolder(folderPath);
                      }}
                      title="Delete folder"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {folder && (
            <div className={`flex items-center gap-2 p-2 rounded-md border ${getFolderUsageColor(folder).bg} ${getFolderUsageColor(folder).border}`}>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${getFolderUsageColor(folder).indicator}`} />
              <span className={`text-sm font-medium ${getFolderUsageColor(folder).text}`}>
                {getFolderUsedSize(folder)} / 25 MB ({getFolderUsagePercent(folder)}% used)
              </span>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <div className="flex-1 h-px bg-violet-200 dark:bg-violet-800"></div>
            <span className="text-xs text-violet-500">or create new</span>
            <div className="flex-1 h-px bg-violet-200 dark:bg-violet-800"></div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
          No folders yet. Create your first folder to organize documents.
        </p>
      )}

      {/* Create new folder */}
      <div className="flex gap-2 mt-3">
        <Input
          placeholder="Enter folder name (e.g., Projects)"
          value={newFolder}
          onChange={(e) => { setNewFolder(e.target.value); if (e.target.value.trim()) setFolder(''); }}
          className="flex-1"
        />
        <Button
          variant="default"
          className="bg-violet-600 hover:bg-violet-700"
          onClick={() => {
            const trimmedPath = newFolder.trim();
            if (!trimmedPath) {
              toast({
                title: 'Folder name required',
                description: 'Please enter a folder name',
                variant: 'destructive',
              });
              return;
            }
            const folderPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
            createFolderMutation.mutate({ folderPath });
            setNewFolder('');
          }}
          disabled={createFolderMutation.isPending || !newFolder.trim()}
        >
          <FolderPlus className="h-4 w-4 mr-2" />
          {createFolderMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </div>
    </div>
  );

  if (userLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (userError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('knowledgeBase.title')}</h1>
        </div>
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">{t('knowledgeBase.unableToLoad')}</h2>
          <p className="text-muted-foreground">
            {t('knowledgeBase.unableToLoadDesc')}
          </p>
        </Card>
      </div>
    );
  }

  const urlCount = knowledgeBase.filter(item => item.type === 'url').length;
  const fileCount = knowledgeBase.filter(item => item.type === 'file').length;
  const textCount = knowledgeBase.filter(item => item.type === 'text').length;
  const processingCount = knowledgeBase.filter(item => item.ragStatus === 'processing').length;
  const totalChunks = knowledgeBase.reduce((sum, item) => sum + (item.chunkCount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">{t('knowledgeBase.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('knowledgeBase.description')}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {/* The cheapest way for a user to check an agent will actually know something.
              POST /search has existed all along with no UI. */}
          <Button
            variant="outline"
            onClick={() => setTestDialogOpen(true)}
            className="border-cyan-300 dark:border-cyan-800 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-950/40"
            data-testid="button-test-question"
          >
            <Search className="h-4 w-4 mr-2" />
            Test a question
          </Button>
          {/* POST /text existed with no UI: the page offered uploads and URLs only. */}
          <Button
            variant="outline"
            onClick={() => setTextDialogOpen(true)}
            className="border-violet-300 dark:border-violet-800 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40"
            data-testid="button-add-text"
          >
            <Type className="h-4 w-4 mr-2" />
            Add text
          </Button>
          <Button
            variant="outline"
            onClick={() => setUrlDialogOpen(true)}
            className="border-violet-300 dark:border-violet-800 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40"
            data-testid="button-add-url"
          >
            <Globe className="h-4 w-4 mr-2" />
            {t('knowledgeBase.actions.addUrl')}
          </Button>
          <Button
            onClick={() => setFileDialogOpen(true)}
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-md"
            data-testid="button-add-files"
          >
            <Upload className="h-4 w-4 mr-2" />
            {t('knowledgeBase.actions.uploadFile')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{knowledgeBase.length}</div>
              <div className="text-slate-500 dark:text-slate-400 text-sm">Total Items</div>
            </div>
            <div className="h-10 w-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Brain className="h-5 w-5 text-violet-600 dark:text-violet-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{fileCount}</div>
              <div className="text-slate-500 dark:text-slate-400 text-sm">Documents</div>
            </div>
            <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <FileText className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{urlCount}</div>
              <div className="text-slate-500 dark:text-slate-400 text-sm">Websites</div>
            </div>
            <div className="h-10 w-10 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
              <Globe className="h-5 w-5 text-sky-600 dark:text-sky-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{totalChunks}</div>
              <div className="text-slate-500 dark:text-slate-400 text-sm">AI Chunks</div>
            </div>
            <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-lg font-bold text-slate-900 dark:text-slate-100">{formatBytes(totalSize)}</div>
              <div className="text-slate-500 dark:text-slate-400 text-xs">/ {formatBytes(storageUsage?.maxStorageBytes || 20 * 1024 * 1024)}</div>
            </div>
            <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <HardDrive className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <Progress
            value={storageUsage?.usagePercent || Math.round((totalSize / (storageUsage?.maxStorageBytes || 20 * 1024 * 1024)) * 100)}
            className="h-1.5 mt-2"
          />
        </div>
      </div>

      {/* Processing Banner */}
      {processingCount > 0 && (
        <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 flex items-center gap-3 border border-blue-200 dark:border-blue-800">
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          <span className="text-sm text-blue-700 dark:text-blue-300">
            {t('knowledgeBase.processing.message', { count: processingCount })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 text-blue-600"
            onClick={() => refetch()}
            data-testid="button-refresh-status"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            {t('common.refresh')}
          </Button>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('knowledgeBase.searchPlaceholder')}
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="input-search-knowledge-base"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2" data-testid="button-filter-type">
              {typeFilter ? t(`knowledgeBase.types.${typeFilter}`) : t('knowledgeBase.filters.allTypes')}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTypeFilter(null)}>
              {t('knowledgeBase.filters.allTypes')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTypeFilter('file')}>
              <FileText className="mr-2 h-4 w-4" />
              {t('knowledgeBase.types.file')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTypeFilter('url')}>
              <Globe className="mr-2 h-4 w-4" />
              {t('knowledgeBase.types.url')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTypeFilter('text')}>
              <Type className="mr-2 h-4 w-4" />
              {t('knowledgeBase.types.text')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2">
              {folderFilter !== '/' && (
                <span className={`w-2 h-2 rounded-full ${getFolderUsageColor(folderFilter).indicator}`} />
              )}
              📁 {folderFilter === '/' ? 'All Folders' : formatFolderDisplay(folderFilter)}
              {folderFilter !== '/' && (
                <span className="text-xs text-muted-foreground">({getFolderUsagePercent(folderFilter)}%)</span>
              )}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuItem onClick={() => setFolderFilter('/')}>
              📁 All Folders
            </DropdownMenuItem>
            {folders.map(folder => {
              const folderPath = typeof folder === 'string' ? folder : (folder as any).path || '/';
              const folderCount = typeof folder === 'object' ? (folder as any).count || 0 : 0;
              // How many agents select this folder. Deleting it strips their knowledge.
              const folderAgentCount = typeof folder === 'object' ? (folder as any).agentCount || 0 : 0;
              const usageColors = getFolderUsageColor(folderPath);
              const usagePercent = getFolderUsagePercent(folderPath);
              const usedSize = getFolderUsedSize(folderPath);
              return (
              <DropdownMenuItem
                key={folderPath}
                className={`flex items-center justify-between gap-2 ${usageColors.bg} ${usageColors.border} border rounded-md my-1 mx-1`}
                onSelect={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('button[data-delete-folder]')) {
                    e.preventDefault();
                    return;
                  }
                  setFolderFilter(folderPath);
                }}
              >
                <div className="flex items-center gap-2 flex-1 cursor-pointer" onClick={() => setFolderFilter(folderPath)}>
                  <span className={`w-2.5 h-2.5 rounded-full ${usageColors.indicator}`} />
                  <span className={`font-medium ${usageColors.text}`}>
                    {formatFolderDisplay(folderPath)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({folderCount} docs)
                  </span>
                  {folderCount === 0 && (
                    <span className="text-xs font-semibold text-yellow-600 bg-yellow-50 border border-yellow-200 rounded px-1 ml-1">
                      empty
                    </span>
                  )}
                  {folderAgentCount > 0 && (
                    <span className="text-xs font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded px-1 ml-1">
                      {folderAgentCount} {folderAgentCount === 1 ? 'agent' : 'agents'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium ${usageColors.text}`}>
                    {usedSize} ({usagePercent}%)
                  </span>
                  <button
                    data-delete-folder="true"
                    className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDeletingFolder(folderPath);
                    }}
                    title="Delete folder"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </DropdownMenuItem>
            ); })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">{t('knowledgeBase.loading')}</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-muted">
            <FileStack className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">{t('knowledgeBase.empty.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {searchQuery || typeFilter ? t('knowledgeBase.empty.adjustFilters') : t('knowledgeBase.empty.uploadFirst')}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('knowledgeBase.table.name')}</TableHead>
                  <TableHead>{t('common.type')}</TableHead>
                  <TableHead>{t('knowledgeBase.table.aiStatus')}</TableHead>
                  {/* Users could not see which agents a document fed, so deleting one
                      silently stripped an agent's knowledge. */}
                  <TableHead className="hidden md:table-cell">Used by</TableHead>
                  <TableHead className="hidden md:table-cell">{t('knowledgeBase.table.chunks')}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t('knowledgeBase.table.size')}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t('knowledgeBase.table.created')}</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedItems.map((item) => (
                  <TableRow key={item.id} data-testid={`row-kb-item-${item.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(item.type)}
                        <span className="truncate max-w-[200px]">{item.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getTypeBadgeColor(item.type)}`}>
                        {getTypeIcon(item.type)}
                        {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(item.ragStatus)}`}>
                            {getStatusIcon(item.ragStatus)}
                            {getStatusText(item.ragStatus)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {item.ragStatus === 'completed'
                            ? t('knowledgeBase.tooltips.indexed')
                            : item.ragStatus === 'processing'
                            ? t('knowledgeBase.tooltips.generating')
                            : item.ragStatus === 'failed'
                            ? (item.ragError || t('knowledgeBase.tooltips.failed'))
                            : t('knowledgeBase.tooltips.waiting')}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {(item.agentCount ?? 0) === 0 ? (
                        <span className="text-xs text-muted-foreground">No agents</span>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-cyan-200 bg-cyan-50 text-cyan-700">
                              <Bot className="h-3 w-3" />
                              {item.agentCount} {item.agentCount === 1 ? 'agent' : 'agents'}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {(item.usedByAgents ?? []).join(', ')}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden md:table-cell">
                      {item.chunkCount || 0}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden sm:table-cell">
                      {formatBytes(item.storageSize)}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden lg:table-cell">
                      {formatDate(item.createdAt, 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {/* A failed document was a dead end: POST /:id/reprocess has
                            existed all along with nothing in the UI to call it, so the
                            only way out was delete and re-upload. */}
                        {item.ragStatus === 'failed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => reprocessMutation.mutate(item.id)}
                            disabled={reprocessMutation.isPending}
                            title={item.ragError || 'Retry processing this document'}
                            data-testid={`button-reprocess-${item.id}`}
                          >
                            <RefreshCw className={`h-4 w-4 ${reprocessMutation.isPending ? 'animate-spin' : ''}`} />
                            <span className="ml-1">Retry</span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMovingItem(item);
                            const metadata = item.metadata as any;
                            setSelectedFolder(metadata?.folderPath || '/');
                            setMoveDialogOpen(true);
                          }}
                        >
                          📁 Move
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewVersions(item)}
                          title="View version history"
                        >
                          📚 Versions
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeletingItem(item)}
                          data-testid={`button-delete-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DataPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </>
      )}

      <Dialog
        open={urlDialogOpen}
        onOpenChange={(open) => {
          setUrlDialogOpen(open);
          if (!open) resetUrlDialog();
        }}
      >
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('knowledgeBase.dialog.addUrl.title')}</DialogTitle>
            <DialogDescription>
              {t('knowledgeBase.dialog.addUrl.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* STEP 1: Folder Selection/Creation (FIRST - Required) */}
            {renderFolderStep(urlFolder, setUrlFolder, urlNewFolder, setUrlNewFolder)}

            {/* STEP 2: Website URL */}
            <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border rounded-lg p-4 transition-opacity ${!urlFolder && !urlNewFolder.trim() ? 'opacity-50 border-gray-200 dark:border-gray-700' : 'border-blue-200 dark:border-blue-800'}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${urlFolder || urlNewFolder.trim() ? 'bg-blue-600 text-white' : 'bg-gray-300 dark:bg-gray-700 text-gray-500'}`}>2</div>
                <h4 className={`font-semibold ${urlFolder || urlNewFolder.trim() ? 'text-blue-900 dark:text-blue-100' : 'text-gray-500'}`}>Add Website URL</h4>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="kb-url" className="text-sm text-blue-700 dark:text-blue-300">{t('knowledgeBase.labels.url')}</Label>
                  <Input
                    id="kb-url"
                    type="url"
                    inputMode="url"
                    placeholder={t('knowledgeBase.placeholders.url')}
                    value={urlValue}
                    onChange={(e) => setUrlValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !addUrlMutation.isPending) {
                        handleAddUrl();
                      }
                    }}
                    disabled={!urlFolder && !urlNewFolder.trim()}
                    data-testid="input-url"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="kb-url-name" className="text-sm text-blue-700 dark:text-blue-300">{t('knowledgeBase.labels.nameOptional')}</Label>
                  <Input
                    id="kb-url-name"
                    placeholder={t('knowledgeBase.placeholders.urlName')}
                    value={urlName}
                    onChange={(e) => setUrlName(e.target.value)}
                    disabled={!urlFolder && !urlNewFolder.trim()}
                    data-testid="input-url-name"
                  />
                </div>
              </div>
            </div>

            {/* What gets imported - Compact */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-800">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400 font-medium">Imports:</span>
                <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded">Visible page text</span>
                <span className="text-gray-400 mx-1">|</span>
                <span className="text-gray-500 dark:text-gray-400">One page per URL &mdash; add each page separately</span>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                The page is fetched once and indexed. Later changes on the website are not picked up automatically &mdash; re-add the URL to refresh it.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => {
                setUrlDialogOpen(false);
                resetUrlDialog();
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              className="w-full sm:w-auto bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              onClick={handleAddUrl}
              disabled={!urlValue.trim() || addUrlMutation.isPending || (!urlFolder && !urlNewFolder.trim())}
              data-testid="button-submit-url"
            >
              {addUrlMutation.isPending ? t('knowledgeBase.actions.adding') : t('knowledgeBase.actions.addUrl')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fileDialogOpen} onOpenChange={setFileDialogOpen}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('knowledgeBase.dialog.uploadFile.title')}</DialogTitle>
            <DialogDescription>
              {t('knowledgeBase.dialog.uploadFile.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* STEP 1: Folder Selection/Creation (FIRST - Required) */}
            {renderFolderStep(fileFolder, setFileFolder, fileNewFolder, setFileNewFolder)}
            {/* STEP 2: File Upload */}
            <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border rounded-lg p-4 transition-opacity ${!fileFolder && !fileNewFolder.trim() ? 'opacity-50 border-gray-200 dark:border-gray-700' : 'border-blue-200 dark:border-blue-800'}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${fileFolder || fileNewFolder.trim() ? 'bg-blue-600 text-white' : 'bg-gray-300 dark:bg-gray-700 text-gray-500'}`}>2</div>
                <h4 className={`font-semibold ${fileFolder || fileNewFolder.trim() ? 'text-blue-900 dark:text-blue-100' : 'text-gray-500'}`}>Upload Document</h4>
              </div>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="file"
                    id="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".txt,.md,.html,.htm,.json,.xml,.csv,.pdf,.docx"
                    className="hidden"
                    disabled={!fileFolder && !fileNewFolder.trim()}
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 min-w-0"
                    disabled={!fileFolder && !fileNewFolder.trim()}
                    data-testid="button-select-file"
                  >
                    <Upload className="h-4 w-4 mr-2 shrink-0" />
                    <span className="truncate">{selectedFile ? selectedFile.name : t('knowledgeBase.actions.chooseFile')}</span>
                  </Button>
                </div>

                {selectedFile && (
                  <p className="text-xs text-muted-foreground">
                    Size: {formatBytes(selectedFile.size)} {selectedFile.size > MAX_FILE_SIZE_BYTES && <span className="text-destructive">(exceeds 10 MB limit)</span>}
                  </p>
                )}

                <div className="space-y-2">
                  <Label htmlFor="file-name" className="text-sm text-blue-700 dark:text-blue-300">{t('knowledgeBase.labels.nameOptional')}</Label>
                  <Input
                    id="file-name"
                    placeholder={t('knowledgeBase.placeholders.fileName')}
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    disabled={!fileFolder && !fileNewFolder.trim()}
                    data-testid="input-file-name"
                  />
                </div>
              </div>
            </div>

            {/* Supported Formats Info - Compact */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-800">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400 font-medium">Formats:</span>
                <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded">PDF</span>
                <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded">DOCX</span>
                <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded">TXT</span>
                <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded">MD</span>
                <span className="px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded">HTML</span>
                <span className="px-1.5 py-0.5 bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300 rounded">CSV</span>
                <span className="text-gray-400 mx-1">|</span>
                <span className="text-gray-500 dark:text-gray-400">Max: <span className="font-semibold text-green-600 dark:text-green-400">10 MB</span></span>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => {
              setFileDialogOpen(false);
              setSelectedFile(null);
              setFileName('');
              setFileFolder('');
              setFileNewFolder('');
            }}>
              {t('common.cancel')}
            </Button>
            <Button
              className="w-full sm:w-auto bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              onClick={() => handleUploadFile()}
              disabled={!selectedFile || uploadFileMutation.isPending || (!fileFolder && !fileNewFolder.trim())}
              data-testid="button-submit-file"
            >
              {uploadFileMutation.isPending ? t('knowledgeBase.actions.uploading') : t('knowledgeBase.actions.uploadFile')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={createFolderDialogOpen} onOpenChange={setCreateFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Create a new folder to organize your knowledge base documents. Folder names must be unique.
              {kbLimits && ` (${kbLimits.folders.current}/${kbLimits.folders.max} folders used)`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-folder-path">Folder Name</Label>
              <Input
                id="new-folder-path"
                placeholder="Projects"
                value={newFolderPath}
                onChange={(e) => setNewFolderPath(e.target.value)}
                data-testid="input-new-folder-path"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCreateFolderDialogOpen(false);
              setNewFolderPath('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                const trimmedPath = newFolderPath.trim();
                if (!trimmedPath) {
                  toast({
                    title: 'Folder name required',
                    description: 'Please enter a folder name',
                    variant: 'destructive',
                  });
                  return;
                }
                
                // Auto-prepend "/" if not present
                const folderPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;

                // Check for duplicate folder name (case-insensitive)
                const isDuplicate = folders.some((f: any) => {
                  const existingPath = typeof f === 'string' ? f : f.path;
                  return existingPath.toLowerCase() === folderPath.toLowerCase();
                });
                if (isDuplicate) {
                  toast({
                    title: 'Folder already exists',
                    description: `A folder named "${trimmedPath}" already exists. Please choose a different name.`,
                    variant: 'destructive',
                  });
                  return;
                }

                createFolderMutation.mutate({ folderPath });
              }}
              disabled={createFolderMutation.isPending || (kbLimits?.folders.remaining === 0)}
              data-testid="button-submit-create-folder"
            >
              {createFolderMutation.isPending ? 'Creating...' : kbLimits?.folders.remaining === 0 ? 'Folder Limit Reached' : 'Create Folder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to Folder</DialogTitle>
            <DialogDescription>
              Choose a folder for "{movingItem?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Folder</Label>
              <select
                className="w-full p-2 border rounded"
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
              >
                {folders.map(folder => { const folderPath = typeof folder === 'string' ? folder : (folder as any).path || '/'; return ( <option key={folderPath} value={folderPath}> 📁 {formatFolderDisplay(folderPath)} </option> ); })}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Or Create New Folder</Label>
              <Input
                placeholder="/Projects/2024"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Use format: /FolderName or /Parent/Child
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                const targetFolder = newFolderName.trim() || selectedFolder;
                if (movingItem) {
                  moveMutation.mutate({ id: movingItem.id, folderPath: targetFolder });
                }
              }}
              disabled={moveMutation.isPending}
            >
              {moveMutation.isPending ? 'Moving...' : 'Move'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={duplicateDialog.open} onOpenChange={() => setDuplicateDialog({ open: false, error: null, pendingAction: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Detected</DialogTitle>
            <DialogDescription>
              {duplicateDialog.error?.message}
            </DialogDescription>
          </DialogHeader>
          {duplicateDialog.error && (
            <div className="space-y-4 py-4">
              <div className="bg-muted rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Existing Document:</p>
                <p className="text-sm text-muted-foreground">
                  <strong>Title:</strong> {duplicateDialog.error.existingDocument.title}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Created:</strong> {formatDate(duplicateDialog.error.existingDocument.createdAt, 'M/d/yyyy, h:mm:ss a')}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">What would you like to do?</p>
                {duplicateDialog.error.actions.map((action) => (
                  <Button
                    key={action.action}
                    variant={action.action === 'replace' ? 'default' : 'outline'}
                    className="w-full"
                    onClick={() => handleDuplicateAction(action.action)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={versionsDialogOpen} onOpenChange={setVersionsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              View and manage versions for "{viewingVersionsFor?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {versionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : versions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">No version history available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {versions.map((version) => (
                  <Card key={version.id} className={`p-4 ${version.isLatest ? 'border-primary' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={version.isLatest ? 'default' : 'outline'}>
                            Version {version.version || 1}
                          </Badge>
                          {version.isLatest && (
                            <Badge variant="secondary">Current</Badge>
                          )}
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            {getStatusIcon(version.ragStatus)}
                            <span>{getStatusText(version.ragStatus)}</span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          <strong>Created:</strong> {formatDate(version.createdAt, 'M/d/yyyy, h:mm:ss a')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Size:</strong> {formatBytes(version.storageSize)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Chunks:</strong> {version.chunkCount || 0}
                        </p>
                      </div>
                      {!version.isLatest && (
                        <Button
                          size="sm"
                          onClick={() => handleRestoreVersion(version.id)}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Restore
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add pasted text — POST /text existed with no UI, so the page offered uploads
          and URLs only and a user with a paragraph of policy had nowhere to put it. */}
      <Dialog open={textDialogOpen} onOpenChange={setTextDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add text</DialogTitle>
            <DialogDescription>
              Paste text your agents should be able to answer from.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="text-name">Title *</Label>
              <Input
                id="text-name"
                value={textName}
                onChange={(e) => setTextName(e.target.value)}
                placeholder="Refund policy"
                data-testid="input-text-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="text-folder">Folder *</Label>
              <select
                id="text-folder"
                className="w-full border rounded-md h-10 px-3 bg-background"
                value={textFolder}
                onChange={(e) => setTextFolder(e.target.value)}
                data-testid="select-text-folder"
              >
                <option value="">Choose a folder...</option>
                {folders.map(folder => (
                  <option key={folder.path} value={folder.path}>
                    📁 {formatFolderDisplay(folder.path)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="text-body">Text *</Label>
              <Textarea
                id="text-body"
                rows={10}
                value={textBody}
                onChange={(e) => setTextBody(e.target.value)}
                placeholder="Paste the content here..."
                data-testid="input-text-body"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTextDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => addTextMutation.mutate()}
              disabled={!textName || !textBody || !textFolder || addTextMutation.isPending}
              data-testid="button-submit-text"
            >
              {addTextMutation.isPending ? 'Adding...' : 'Add text'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test a question — shows the chunks an agent would actually retrieve. */}
      <Dialog
        open={testDialogOpen}
        onOpenChange={(open) => {
          setTestDialogOpen(open);
          if (!open) setTestResults(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Test a question</DialogTitle>
            <DialogDescription>
              Ask what a caller might ask, and see exactly what an agent using this folder
              would retrieve.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test-folder">Folder</Label>
              <select
                id="test-folder"
                className="w-full border rounded-md h-10 px-3 bg-background"
                value={testFolder}
                onChange={(e) => { setTestFolder(e.target.value); setTestResults(null); }}
                data-testid="select-test-folder"
              >
                <option value="">Choose a folder...</option>
                {folders.map(folder => (
                  <option key={folder.path} value={folder.path}>
                    📁 {formatFolderDisplay(folder.path)} ({folder.count} docs)
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="test-question">Question</Label>
              <Input
                id="test-question"
                value={testQuestion}
                onChange={(e) => setTestQuestion(e.target.value)}
                placeholder="What is your refund window?"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && testFolder && testQuestion) testSearchMutation.mutate();
                }}
                data-testid="input-test-question"
              />
            </div>

            {testResults && (
              <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="test-results">
                {testResults.results.length === 0 ? (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    Nothing matched. An agent asked this would say it does not know.
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {testResults.results.length} chunk(s) retrieved
                      {testResults.confidence ? ` · confidence: ${testResults.confidence}` : ''}
                    </p>
                    {testResults.results.map((r, i) => (
                      <div key={i} className="rounded-md border p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-muted-foreground">
                            {knowledgeBase.find(k => k.id === r.source)?.title || r.source}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            score {r.score.toFixed(2)}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap text-muted-foreground">{r.text}</p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
              {t('common.close')}
            </Button>
            <Button
              onClick={() => testSearchMutation.mutate()}
              disabled={!testFolder || !testQuestion || testSearchMutation.isPending}
              data-testid="button-run-test"
            >
              {testSearchMutation.isPending ? 'Searching...' : 'Search'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingItem} onOpenChange={() => setDeletingItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('knowledgeBase.dialog.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('knowledgeBase.dialog.delete.description', { title: deletingItem?.title })}
            </AlertDialogDescription>
            {/* Name the agents BEFORE the delete, not after. This connection was
                invisible, so users deleted a document and only discovered which agent
                had gone quiet on the next call. */}
            {(deletingItem?.agentCount ?? 0) > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 mt-2">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900">
                  <p className="font-medium">
                    {deletingItem!.agentCount} agent{deletingItem!.agentCount === 1 ? '' : 's'} will
                    lose this document
                  </p>
                  <p className="mt-1 text-xs">{(deletingItem!.usedByAgents ?? []).join(', ')}</p>
                </div>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingItem && deleteMutation.mutate(deletingItem.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deletingFolder} onOpenChange={() => setDeletingFolder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Folder</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                // Matched through the shared normalizer: the sidebar renders whatever
                // spelling the server returned, and === missed "/Sales" vs "Sales".
                const folderData: any = folders.find(
                  (f: any) => folderPathKey(typeof f === 'string' ? f : f.path) === folderPathKey(deletingFolder),
                );
                const docCount = folderData?.count ?? 0;
                const name = deletingFolder ? formatFolderDisplay(deletingFolder) : '';
                return docCount > 0
                  ? `Are you sure you want to delete the folder "${name}" and all ${docCount} document(s) inside it? This action cannot be undone.`
                  : `Are you sure you want to delete the folder "${name}"? This action cannot be undone.`;
              })()}
            </AlertDialogDescription>
            {(() => {
              const folderData: any = folders.find(
                (f: any) => folderPathKey(typeof f === 'string' ? f : f.path) === folderPathKey(deletingFolder),
              );
              const agentCount = folderData?.agentCount ?? 0;
              if (agentCount === 0) return null;
              return (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 mt-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-900">
                    <p className="font-medium">
                      {agentCount} agent{agentCount === 1 ? '' : 's'} select this folder
                    </p>
                    <p className="mt-1 text-xs">
                      {agentCount === 1 ? 'It' : 'They'} will be left with no knowledge base
                      until you choose another folder in the agent's settings.
                    </p>
                  </div>
                </div>
              );
            })()}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingFolder && deleteFolderMutation.mutate(deletingFolder)}
              disabled={deleteFolderMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteFolderMutation.isPending ? 'Deleting...' : 'Delete Folder'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
